/**
 * barba.js - route-to-tab mapping and page transition orchestration.
 */

(function (global) {
  'use strict';

  // ── Namespace → tab mapping ─────────────────────────────────
  // Maps Barba namespace (= {{ template.name | handleize }}) to active tab id.
  const NAMESPACE_TAB_MAP = {
    'index':            'none',
    'collection':       'shop',
    'product':          'shop',
    'list-collections': 'shop',
    'search':           'shop',  // search lives under shop drawer
    'blog':             'news',
    'article':          'news',
    'page':             'info',
    '404':              '404',
    'cart':             'cart',
  };

  // ── Mobile panel-top helper ────────────────────────────────
  // Maps each tab-id to the number of mobile tab-strips that sit
  // ABOVE the panel (1 for shop, 2 for news, 3 for everything else).
  const MOB_TAB_ROWS = {
    shop: 1, news: 2, info: 3, cart: 3, '404': 3, none: 3,
  };

  function isMobile() {
    return window.matchMedia('(max-width: 768px)').matches;
  }

  function getTabForNamespace(ns) {
    if (NAMESPACE_TAB_MAP[ns]) return NAMESPACE_TAB_MAP[ns];
    if (typeof ns === 'string' && ns.indexOf('page-') === 0) return 'info';
    return 'none';
  }

  function normalizePath(path) {
    if (!path) return '/';
    const normalized = path.replace(/\/+$/, '');
    return normalized || '/';
  }

  function tabFromMainRoute(path) {
    const p = normalizePath(path);
    if (/^\/collections(\/|$)/.test(p)) return 'shop';
    if (p === '/blogs/news') return 'news';
    if (p === '/pages/information') return 'info';
    return null;
  }

  function shouldInstantMainTabSwitch(data) {
    if (!data || !data.current || !data.next) return false;

    // Only bypass animation while the panel is already up.
    if (getActiveTab() === 'none') return false;

    const fromTab = tabFromMainRoute(data.current.url && data.current.url.path);
    const toTab = tabFromMainRoute(data.next.url && data.next.url.path);

    return Boolean(fromTab && toTab && fromTab !== toTab);
  }

  // ── Shell helpers ────────────────────────────────────────────
  const shell = () => document.getElementById('site-shell');

  function setActiveNamespace(ns) {
    const s = shell();
    if (!s) return;
    s.dataset.activeNamespace = ns || 'index';
  }

  function setActiveTab(tabId) {
    const s = shell();
    if (!s) return;
    s.dataset.activeTab = tabId;
    // Mobile: update the CSS var that controls panel top-position.
    // The CSS rule reads: top = header-h + (--mob-panel-top-n) * mob-tab-h
    if (isMobile()) {
      const n = MOB_TAB_ROWS[tabId] !== undefined ? MOB_TAB_ROWS[tabId] : 3;
      document.documentElement.style.setProperty('--mob-panel-top-n', n);
    }
  }

  function getActiveTab() {
    return shell() ? shell().dataset.activeTab : 'none';
  }

  // ── Prevent / deny rules ─────────────────────────────────────

  /**
   * Return true from barba.init prevent to BLOCK Barba from intercepting the link.
   */
  function shouldPrevent({ el, url, href }) {
    if (!el) return true;

    const link = el.tagName === 'A' ? el : el.closest('a');
    if (!link) return true;

    const rawHref = link.getAttribute('href') || '';

    // ① Non-http protocols (mailto, tel, javascript, etc.)
    if (/^(mailto:|tel:|javascript:|data:)/i.test(rawHref)) return true;

    // ② External link or different origin
    try {
      const parsed = new URL(href, window.location.origin);
      if (parsed.origin !== window.location.origin) return true;
    } catch (e) {
      return true;
    }

    const path = new URL(href, window.location.origin).pathname;

    // ③a /cart links — open the drawer instead of navigating
    if (path === '/cart' || path === '/cart/') {
      setTimeout(function () {
        if (global.XericCart) {
          global.XericCart.open();
        }
      }, 0);
      return true; // block Barba navigation
    }

    // ③ Shopify system routes — ALWAYS deny
    const DENIED_PREFIXES = [
      '/checkout',
      '/account',
      '/challenge',
      '/password',
      '/apps/',
    ];
    if (DENIED_PREFIXES.some(p => path.startsWith(p))) return true;

    // ④ File downloads
    if (/\.(pdf|zip|xls|xlsx|docx?|pptx?|csv)(\?|$)/i.test(path)) return true;

    // ⑤ Rewrite bare /collections → /collections/featured-products
    //    Prevent Barba from navigating there, then push the correct URL.
    if (path === '/collections' || path === '/collections/') {
      setTimeout(function () {
        if (global.barba) {
          global.barba.go('/collections/featured-products');
        } else {
          window.location.href = '/collections/featured-products';
        }
      }, 0);
      return true; // block original navigation
    }

    // ⑥ Hash-only anchor (same URL, different fragment)
    if (
      rawHref.startsWith('#') ||
      (link.hash && link.pathname === window.location.pathname)
    ) return true;

    // ⑦ Modal links — XericModal handles these via capture-phase click delegation.
    //    Belt-and-suspenders: also block Barba here in case event order shifts.
    if (link.hasAttribute('data-modal')) return true;

    // ⑦ Shopify theme editor — degrade gracefully
    if (global.Shopify && global.Shopify.designMode) return true;

    return false;
  }

  // ── Transition: Home → Tab ───────────────────────────────────
  const homeToTabTransition = {
    name: 'home-to-tab',
    from: { namespace: ['index'] },

    async leave({ current, next }) {
      // Set active tab immediately — CSS background fires on next.container.
      // No fade: the panel slides up opaque and covers the home scene naturally.
      const tab = getTabForNamespace(next.namespace);
      setActiveTab(tab);
      setActiveNamespace(next.namespace);
      // Ensure next.container is fully opaque (clears any prior GSAP opacity state).
      gsap.set(next.container, { opacity: 1 });
    },

    async enter({ next }) {
      // Slide the fully-opaque colored panel up — no fade needed.
      await XericAnimations.panelOpen(next.container);
    },
  };

  // ── Transition: Tab → Home ───────────────────────────────────
  const tabToHomeTransition = {
    name: 'tab-to-home',
    to: { namespace: ['index'] },

    async leave({ current, next }) {
      // Ensure next (home) container is opaque and snapped closed offscreen.
      gsap.set(next.container, { opacity: 1 });
      // Slide the current colored panel down — no fade, solid throughout.
      // Keep active tab color during slide so panel stays opaque until docked.
      await XericAnimations.panelClose(current.container);
    },

    async enter({ next }) {
      // Panel is now docked — clear tab color to reveal home scene.
      setActiveTab('none');
      setActiveNamespace(next.namespace);
      XericAnimations.panelSnap(next.container, 'closed');
    },
  };

  // ── Transition: Tab → Tab (new page slides up from dock) ──────
  const tabToTabTransition = {
    name: 'tab-to-tab',

    leave({ current, next }) {
      // Mobile: pin current container's CSS top as inline style so the
      // upcoming setActiveTab() call doesn't shift it via the CSS selector.
      if (isMobile()) {
        current.container.style.top = getComputedStyle(current.container).top;
        // Tell panelOpen() which tabs were already above the panel so it can
        // leave them in place and only animate the newly-revealed tab(s).
        XericAnimations.setPrevTab(getActiveTab());
      }
      // Pin the current container's background before switching active-tab so
      // the CSS selector can't recolor it while both containers are in the DOM.
      current.container.style.backgroundColor = getComputedStyle(current.container).backgroundColor;
      const tab = getTabForNamespace(next.namespace);
      setActiveTab(tab);
      setActiveNamespace(next.namespace);
      gsap.set(current.container, { opacity: 1 });
      // Position next container at the docked/bottom edge — tabs stay at y=0.
      XericAnimations.panelPrepNext(next.container, current.container);
    },

    async enter({ next }) {
      // Slide the incoming page up from the dock, same motion as a tab click.
      await XericAnimations.panelOpen(next.container);
    },
  };

  // ── Transition: Main tab root ↔ main tab root (instant) ─────
  const instantMainTabSwitchTransition = {
    name: 'instant-main-tab-switch',

    custom: shouldInstantMainTabSwitch,

    leave({ current, next }) {
      const tab = getTabForNamespace(next.namespace);
      setActiveTab(tab);
      setActiveNamespace(next.namespace);

      // Keep both containers snapped with no slide for this specific route pair.
      if (current && current.container) {
        gsap.set(current.container, { opacity: 1, y: 0 });
      }
      if (next && next.container) {
        gsap.set(next.container, { opacity: 1, y: 0 });
      }
    },

    enter({ next }) {
      XericAnimations.panelSnap(next.container, 'open');
    },
  };

  // ── Barba Views ──────────────────────────────────────────────
  // Views run page-specific code using the namespace.

  const views = [
    {
      namespace: 'index',
      afterEnter({ next }) {
        setActiveTab('none');
        setActiveNamespace(next.namespace);
        XericAnimations.panelSnap(next.container, 'closed');
      },
    },
    {
      namespace: 'collection',
      afterEnter({ next }) {
        setActiveTab('shop');
        setActiveNamespace(next.namespace);
        XericAnimations.panelSnap(next.container, 'open');
      },
    },
    {
      namespace: 'product',
      afterEnter({ next }) {
        setActiveTab('shop');
        setActiveNamespace(next.namespace);
        XericAnimations.panelSnap(next.container, 'open');
      },
    },
    {
      namespace: 'list-collections',
      afterEnter({ next }) {
        setActiveTab('shop');
        setActiveNamespace(next.namespace);
        XericAnimations.panelSnap(next.container, 'open');
      },
    },
    {
      namespace: 'search',
      afterEnter({ next }) {
        setActiveTab('shop');
        setActiveNamespace(next.namespace);
        XericAnimations.panelSnap(next.container, 'open');
      },
    },
    {
      namespace: 'blog',
      afterEnter({ next }) {
        setActiveTab('news');
        setActiveNamespace(next.namespace);
        XericAnimations.panelSnap(next.container, 'open');
      },
    },
    {
      namespace: 'article',
      afterEnter({ next }) {
        setActiveTab('news');
        setActiveNamespace(next.namespace);
        XericAnimations.panelSnap(next.container, 'open');
      },
    },
    {
      namespace: 'page',
      afterEnter({ next }) {
        setActiveTab('info');
        setActiveNamespace(next.namespace);
        XericAnimations.panelSnap(next.container, 'open');
      },
    },
    {
      namespace: '404',
      afterEnter({ next }) {
        setActiveTab('404');
        setActiveNamespace(next.namespace);
        XericAnimations.panelSnap(next.container, 'open');
      },
    },
  ];

  // ── XericBarba public API ─────────────────────────────────────

  const XericBarba = {

    setActiveTab,
    getTabForNamespace,
    getActiveTab,

    init() {
      if (!global.barba) {
        console.warn('[XericBarba] barba.umd.js not loaded.');
        return;
      }

      // Theme editor: bypass Barba entirely
      if (global.Shopify && global.Shopify.designMode) {
        console.log('[XericBarba] Design mode detected — Barba disabled.');
        return;
      }

      barba.init({
        // ── Prevent function ──────────
        prevent: shouldPrevent,

        // ── Transitions ───────────────
        transitions: [
          homeToTabTransition,
          tabToHomeTransition,
          instantMainTabSwitchTransition,
          tabToTabTransition,
        ],

        // ── Views ─────────────────────
        views: views,

        // ── Debug (remove in production) ──
        // debug: true,
      });

      // ── Global lifecycle hooks ─────────────────────────────
      // destroyAll → runs before every leave (also covers back/forward)
      barba.hooks.before(() => {
        // Close cart drawer on any navigation
        if (global.XericCart && global.XericCart.isOpen) {
          global.XericCart.close();
        }
      });

      barba.hooks.beforeLeave(() => {
        if (global.XericApp) {
          global.XericApp.destroyAll();
        }
      });

      barba.hooks.afterEnter(({ next }) => {
        if (global.XericApp) {
          global.XericApp.initAll(next.container);
        }
        // Reset scroll position inside the new container
        if (next.container) {
          next.container.scrollTop = 0;
        }
      });

      // Handle browser back/forward — ensure active-tab is correct
      barba.hooks.after(({ next }) => {
        const tab = getTabForNamespace(next.namespace);
        setActiveTab(tab);
        setActiveNamespace(next.namespace);
      });
    },

  };

  global.XericBarba = XericBarba;

})(window);
